/**
 * CheckboxRow: the record form's flag, a self-labelled checkbox row (the
 * label sits beside the box, and the whole label is the tap target, 44px
 * tall). Shared by the record form (RowEditDialog) and the confirm queue's
 * Adjust, so a flag reads the same on both.
 *
 * Billing contact is the office's to set: for a seat without set_bill_to
 * the row is the fact where the box would be, with "The office sets who
 * receives the bill." and no box, so the flag is never sent from it. With
 * set_bill_to, what the box does is the BillingHint line, which the form
 * shows below its roles group while the box is ticked, never in the row
 * (a line in one cell throws the grid off).
 */
import type { ReactNode } from 'react';

export interface CheckboxRowProps {
  id: string;
  testId?: string;
  /** The words beside the box. */
  label: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** This row is Billing contact (is_billing). */
  billing?: boolean;
  /** The seat holds set_bill_to; read only when billing is set. */
  mayChooseBillTo?: boolean;
  disabled?: boolean;
}

export function CheckboxRow({ id, testId, label, checked, onChange, billing = false, mayChooseBillTo = false, disabled = false }: CheckboxRowProps): ReactNode {
  if (billing && !mayChooseBillTo) {
    return (
      <div className="flex flex-col gap-0.5" data-testid="is-billing-office-line">
        <span className="inline-flex min-h-11 items-center text-value text-sage-800">{label}</span>{' '}
        <p className="text-xs text-ink-soft">The office sets who receives the bill.</p>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-0.5">
      <label htmlFor={id} className="inline-flex min-h-11 cursor-pointer items-center gap-2.5 text-value text-sage-800">
        <input
          id={id}
          data-testid={testId}
          type="checkbox"
          className="h-5 w-5 shrink-0 accent-sage-700"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
        />
        {label}
      </label>
    </div>
  );
}

/** What Billing contact does: one quiet line, shown below the roles while
 *  the box is ticked. */
export function BillingHint({ className = '' }: { className?: string }): ReactNode {
  return (
    <p className={`text-xs text-ink-soft ${className}`.trim()} data-testid="is-billing-help">
      Billing contact fills the bill-to on Pre-billing.
    </p>
  );
}
